// CB:BEGIN agreement-viewer
/**
 * AgreementViewer — fetches canonical agreement from API and renders it.
 * Replaces hardcoded JSX agreement text throughout the app.
 *
 * Props:
 *   agreementType: 'contractor-platform' (more types can be added)
 *   version: number (default: current)
 *   showAcceptCheckbox: boolean (default: false)
 *   onAccept: callback when checkbox clicked
 *   accepted: controlled state for checkbox
 */
import React, { useEffect, useState, useCallback } from 'react';
import { getApiUrl } from '../lib/getApiUrl';

interface AgreementViewerProps {
  agreementType?: 'contractor-platform' | 'homeowner-project';
  version?: number;
  showAcceptCheckbox?: boolean;
  onAccept?: (accepted: boolean) => void;
  accepted?: boolean;
  /** Max height for scrollable agreement box. Default: 480px. Pass 'none' to expand fully. */
  maxHeight?: string | number;
}

interface AgreementData {
  agreementType: string;
  version: number;
  effectiveDate: string;
  lastUpdatedDate?: string;
  currentVersion: number;
  content: string;
  format: 'markdown';
}

/** Minimal inline markdown → React element renderer (no external deps). */
function renderMarkdown(md: string): React.ReactNode[] {
  const lines = md.split('\n');
  const nodes: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Strip frontmatter block
    if (i === 0 && line.trim() === '---') {
      while (i < lines.length && !(lines[i].trim() === '---' && i > 0)) i++;
      i++;
      continue;
    }

    // Heading 1
    if (/^# /.test(line)) {
      nodes.push(<h1 key={i} style={{ fontSize: 22, fontWeight: 700, margin: '20px 0 8px', color: '#1f2937' }}>{inlineFormat(line.slice(2))}</h1>);
      i++; continue;
    }
    // Heading 2
    if (/^## /.test(line)) {
      nodes.push(<h2 key={i} style={{ fontSize: 17, fontWeight: 700, margin: '18px 0 6px', color: '#1f2937', borderBottom: '1px solid #e5e7eb', paddingBottom: 4 }}>{inlineFormat(line.slice(3))}</h2>);
      i++; continue;
    }
    // Heading 3
    if (/^### /.test(line)) {
      nodes.push(<h3 key={i} style={{ fontSize: 14, fontWeight: 700, margin: '14px 0 4px', color: '#374151' }}>{inlineFormat(line.slice(4))}</h3>);
      i++; continue;
    }
    // Horizontal rule
    if (/^---$/.test(line.trim())) {
      nodes.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '12px 0' }} />);
      i++; continue;
    }
    // Bullet list — collect consecutive bullet lines
    if (/^- /.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^(\s*)- /.test(lines[i])) {
        const indent = (lines[i].match(/^(\s*)/)?.[1].length ?? 0) > 0;
        items.push(<li key={i} style={{ marginBottom: 4, marginLeft: indent ? 16 : 0 }}>{inlineFormat(lines[i].replace(/^\s*- /, ''))}</li>);
        i++;
      }
      nodes.push(<ul key={`ul-${i}`} style={{ paddingLeft: 20, marginBottom: 8 }}>{items}</ul>);
      continue;
    }
    // Numbered list
    if (/^\d+\. /.test(line)) {
      const items: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(<li key={i} style={{ marginBottom: 4 }}>{inlineFormat(lines[i].replace(/^\d+\. /, ''))}</li>);
        i++;
      }
      nodes.push(<ol key={`ol-${i}`} style={{ paddingLeft: 20, marginBottom: 8 }}>{items}</ol>);
      continue;
    }
    // Blank line
    if (line.trim() === '') {
      i++; continue;
    }
    // Plain paragraph
    nodes.push(<p key={i} style={{ margin: '0 0 8px', lineHeight: 1.6, fontSize: 14, color: '#374151' }}>{inlineFormat(line)}</p>);
    i++;
  }

  return nodes;
}

/** Handle bold, italic, inline code within a line. */
function inlineFormat(text: string): React.ReactNode {
  // Split on **bold**, *italic*, `code`
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, idx) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={idx}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(part)) return <em key={idx}>{part.slice(1, -1)}</em>;
    if (/^`[^`]+`$/.test(part)) return <code key={idx} style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3, fontSize: 12 }}>{part.slice(1, -1)}</code>;
    return part;
  });
}

const AgreementViewer: React.FC<AgreementViewerProps> = ({
  agreementType = 'contractor-platform',
  version,
  showAcceptCheckbox = false,
  onAccept,
  accepted = false,
  maxHeight = 480,
}) => {
  const [data, setData] = useState<AgreementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const url = getApiUrl(`/api/agreements/${agreementType}${version ? `?version=${version}` : ''}`);
    fetch(url, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d: AgreementData) => setData(d))
      .catch((e) => setError(e?.message ?? 'Failed to load agreement'))
      .finally(() => setLoading(false));
  }, [agreementType, version]);

  const handleCheckbox = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onAccept?.(e.target.checked);
  }, [onAccept]);

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>
        Loading agreement…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626' }}>
        Could not load agreement. Please refresh or contact support.
      </div>
    );
  }

  const boxStyle: React.CSSProperties = maxHeight === 'none'
    ? { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '20px 24px' }
    : {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: '20px 24px',
        maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
        overflowY: 'auto',
      };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 4 }}>
          Version {data.version} · Effective {data.effectiveDate}
          {data.lastUpdatedDate && data.lastUpdatedDate !== data.effectiveDate && ` · Last updated ${data.lastUpdatedDate}`}
        </span>
      </div>
      {agreementType !== 'homeowner-project' && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 13, color: '#1e40af' }}>
          You must verify measurements and code requirements yourself before bidding and performing work.
        </div>
      )}
      <div style={boxStyle}>
        {renderMarkdown(data.content)}
      </div>
      {showAcceptCheckbox && (
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, cursor: 'pointer', fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
          <input
            type="checkbox"
            checked={accepted}
            onChange={handleCheckbox}
            style={{ marginTop: 2 }}
          />
          <span>
            I have read and agree to the <strong>CoreBids Contractor Platform Agreement</strong> (Version {data.version}, effective {data.effectiveDate}).
            I understand that my acceptance is recorded with timestamp, IP address, and user agent.
          </span>
        </label>
      )}
    </div>
  );
};

export default AgreementViewer;
// CB:END agreement-viewer
